/**
 * The one navigation bar for every /app screen. Module-aware (links only
 * render for enabled modules), role-aware (warehouse users see just their
 * surface), active-state via the router. Replaces the per-page hand-rolled
 * link rows that each showed a different subset of the app.
 */
import { Link, useMatchRoute } from '@tanstack/react-router';
import { useMe } from '../lib/auth';
import { isNavItemVisible } from '../lib/nav';

interface NavLink {
  to: string;
  label: string;
  /** Module gate; undefined = always shown for tenant-side roles. */
  module?: string;
  warehouseVisible?: boolean;
  roles?: readonly string[];
  exact?: boolean;
}

export const TENANT_LINKS: NavLink[] = [
  { to: '/app', label: 'Today', exact: true },
  { to: '/app/brands', label: 'Brands' },
  { to: '/app/pipeline', label: 'Pipeline' },
  { to: '/app/orders', label: 'Orders', module: 'orders' },
  {
    to: '/app/receiving',
    label: 'To receive',
    module: 'warehouse',
    warehouseVisible: true,
    exact: true,
  },
  {
    to: '/app/warehouse/prep',
    label: 'Prep workstation',
    module: 'warehouse',
    warehouseVisible: true,
    exact: true,
  },
  {
    to: '/app/warehouse/packing',
    label: 'Amazon shipment packing',
    module: 'warehouse',
    warehouseVisible: true,
    exact: true,
  },
  {
    to: '/app/warehouse/charges',
    label: 'Work charges',
    module: 'warehouse',
    warehouseVisible: true,
    exact: true,
  },
  {
    to: '/app/warehouse/invoices',
    label: 'Invoices',
    module: 'warehouse',
    warehouseVisible: true,
    exact: true,
  },
  { to: '/app/shipments', label: 'Shipments', module: 'shipments' },
  { to: '/app/amazon', label: 'Amazon', module: 'amazon' },
  { to: '/app/sales', label: 'Sales', module: 'amazon' },
  { to: '/app/price-calibration', label: 'Price Calibration', module: 'amazon' },
  {
    to: '/app/finance/fee-center',
    label: 'Fee Center',
    module: 'amazon',
    roles: ['owner', 'operator'],
  },
];

const linkBase =
  'text-sm text-fg-secondary hover:text-fg-primary px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors duration-fast';
const linkActive = 'text-sm bg-anchor text-canvas px-3.5 py-1.5 rounded-full whitespace-nowrap';

export function AppNav() {
  const { data } = useMe();
  const matchRoute = useMatchRoute();
  const isWarehouse = data?.user.role === 'warehouse';
  const navCtx = {
    role: data?.user.role,
    modules: data?.modules,
    permissions: data?.user.permissions ?? [],
  };
  const links = TENANT_LINKS.filter((l) => isNavItemVisible(l, navCtx));

  return (
    <nav className="bg-canvas/90 backdrop-blur-sm sticky top-0 z-10">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-3 flex items-center gap-5">
        <Link
          to={isWarehouse ? '/app/receiving' : '/app'}
          className="text-sm font-semibold tracking-tight text-fg-primary shrink-0"
        >
          Sourcito
        </Link>
        <div className="bg-surface border border-border-subtle rounded-full px-1.5 py-1 flex items-center gap-1 overflow-x-auto">
          {links.map((l) => {
            const active = !!matchRoute({ to: l.to, fuzzy: !(l.exact ?? false) });
            return (
              <Link
                key={l.to}
                to={l.to}
                className={active ? linkActive : linkBase}
                aria-current={active ? 'page' : undefined}
              >
                {l.label}
              </Link>
            );
          })}
        </div>
        {!isWarehouse && (
          <Link
            to="/app/settings"
            className={
              matchRoute({ to: '/app/settings', fuzzy: true })
                ? `ml-auto ${linkActive}`
                : `ml-auto ${linkBase}`
            }
            aria-current={matchRoute({ to: '/app/settings', fuzzy: true }) ? 'page' : undefined}
          >
            Settings
          </Link>
        )}
      </div>
    </nav>
  );
}
